import { calculateStoreRecommendations as calculateRecommendations } from '../utils/recommendationLogic';
import graphqlClient from './graphqlClient';

export interface Geo {
  city?: string | null;
  state?: string | null;
  county?: string | null;
  state_code?: string | null;
  crime_rate?: string | null;
  total_crime_incidents?: number | null;
  no_of_cold_days?: number | null;
  no_of_hot_days?: number | null;
  penalty_for_cart_owner?: boolean | null;
  law_summary?: string | null;
  law_source?: string | null;
}

export interface NearbyPenetration {
  penetration?: number | null;
  purchek_customer_count?: number | null;
  penetration_purchek?: number | null;
  containment_customer_count?: number | null;
  penetration_containment?: number | null;
  plaza_cm_customer_count?: number | null;
  penetration_paired_wheel?: number | null;
  shoplifting_amount_usd?: number | null;
  shoplifting_incident_count?: number | null;
  highway_proximity?: string | null;
  total_density?: number | null;
}

export interface Video {
  total_crime_amount_usd?: number | null;
  incident_count?: number | null;
}

export interface BrandStore {
  id?: string;
  site: string;
  address?: string | null;
  zipcode?: number | string | null;
  channel?: string | null;
  // customer_id present => the POI belongs to a known customer
  customer_id?: number | string | null;
  // Install dates for different solutions (nullable strings)
  purchek_install_date?: string | null;
  containment_install_date?: string | null;
  cart_manager_install_date?: string | null;
  subscription_start_date?: string | null;
  paired_wheel_install_date?: string | null;
  // Additional fields
  geo?: Geo | null;
  nearby_penetration?: NearbyPenetration | null;
  video?: Video | null;
  // Rank fields
  rank?: number | null;
  cart_manager_rank?: number | null;
  purchek_rank?: number | null;
  containment_rank?: number | null;
  // Brand-level estimated carts (attached to each store for convenience)
  estimated_no_of_carts_per_store?: number | null;
  wheel?: { single_wheel_replacement_rate?: number | null; service_frequency?: number | null } | null;
  brand_name?: string | null;
  lifetime_value?: number | null;
  first_purchase?: string | null;
  last_purchase?: string | null;
  // Store notes
  store_notes?: Array<{
    id: string;
    comment: string;
    created_at?: string;
    updated_at?: string;
    user?: {
      id: string;
      name: string;
    };
  }> | null;
  updated_store_ids?: Array<{
    store_id: string;
    created_at?: string;
  }> | null;
}

export interface BrandSitesResponse {
  udm_brand: Array<{
    name?: string;
    is_top_retailer?: boolean;
    estimated_no_of_carts_per_store?: number | null;
    total_store?: BrandStore[] | null;
  }>;
}

/**
 * Fetch stores (total_store) for a specific brand.
 * @param brandName - The brand name to fetch stores for (required)
 */
export const fetchBrandStores = async (brandName: string): Promise<BrandStore[]> => {
  try {
    const query = `
      query BrandStores($brandName: String!) @cached {
        udm_brand(where: { name: { _eq: $brandName } }) {
          name
          is_top_retailer
          estimated_no_of_carts_per_store
          # Alias the pois field to total_store per GraphiQL schema
          total_store: pois(where: { closed_on: { _is_null: true } }) {
            id
            site
            address
            zipcode
            channel
            customer_id
            lifetime_value
            latitude
            longitude
            rank
            cart_manager_rank
            purchek_rank
            containment_rank
            purchek_install_date
            containment_install_date
            cart_manager_install_date
            subscription_start_date
            paired_wheel_install_date
            first_purchase
            last_purchase
            geo {
              city
              state
              county
              state_code
              crime_rate
              total_crime_incidents
              no_of_cold_days
              no_of_hot_days
              penalty_for_cart_owner
              law_summary
              law_source
            }
            nearby_penetration {
              penetration
              purchek_customer_count
              penetration_purchek
              containment_customer_count
              penetration_containment
              plaza_cm_customer_count
              penetration_paired_wheel
              shoplifting_amount_usd
              shoplifting_incident_count
              highway_proximity
              total_density
            }
            wheel {
              single_wheel_replacement_rate
              service_frequency
            }
            video {
              total_crime_amount_usd
              incident_count
            }
            store_notes(order_by: {created_at: desc}) {
              id
              comment
              created_at
              updated_at
              user {
                id
                name
              }
            }
            updated_store_ids(order_by: {created_at: desc}, limit: 1) {
              store_id
              created_at
            }
          }
        }
      }
    `;

    const response = await graphqlClient.request<BrandSitesResponse>(query, { brandName });

    if (!response.udm_brand || response.udm_brand.length === 0) {
      return [];
    }

    const combined = response.udm_brand.flatMap((b) => {
      const estimatedCarts = b.estimated_no_of_carts_per_store;
      return (b.total_store ?? []).map(store => ({
        ...store,
        estimated_no_of_carts_per_store: estimatedCarts,
        brand_name: b.name,
        plaza_cm_customer_count: store.nearby_penetration?.plaza_cm_customer_count ?? null,
        purchekCustomerCount: store.nearby_penetration?.purchek_customer_count ?? 0,
        purchekPenetration: store.nearby_penetration?.penetration_purchek ?? 0,
        containmentCustomerCount: store.nearby_penetration?.containment_customer_count ?? 0,
        containmentPenetration: store.nearby_penetration?.penetration_containment ?? 0,
        pairedWheelPenetration: store.nearby_penetration?.penetration_paired_wheel ?? 0,
        singleWheelReplacementRate: store.wheel?.single_wheel_replacement_rate ?? 0,
        serviceFrequency: store.wheel?.service_frequency ?? 0,
        crimeIncidents: store.geo?.total_crime_incidents ?? 0,
        coldDays: store.geo?.no_of_cold_days ?? 0,
        hotDays: store.geo?.no_of_hot_days ?? 0,
        shopliftingAmountUsd: store.nearby_penetration?.shoplifting_amount_usd ?? null,
        shopliftingIncidentCount: store.nearby_penetration?.shoplifting_incident_count ?? null,
        totalCrimeAmountUsd: store.video?.total_crime_amount_usd ?? null,
        videoIncidentCount: store.video?.incident_count ?? null,
        highwayProximity: store.nearby_penetration?.highway_proximity ?? null,
        totalDensity: store.nearby_penetration?.total_density ?? null
      }));
    });

    // Stores already have latitude and longitude from the pois table
    return combined;
  } catch (error) {
    console.error('fetchBrandStores error', error);
    return [];
  }
};

/**
 * Calculate crime distribution from store data
 * Categories: Critical (>=5000), High (>=1000), Medium (>=200), Low (>0), No Data (0)
 */
const calculateCrimeDistribution = (stores: any[]) => {
  const distribution = {
    critical: { count: 0, label: 'Critical', percentage: 0 },
    high: { count: 0, label: 'High', percentage: 0 },
    medium: { count: 0, label: 'Medium', percentage: 0 },
    low: { count: 0, label: 'Low', percentage: 0 },
    noData: { count: 0, label: 'No Data', percentage: 0 }
  };

  const totalStores = stores.length;

  stores.forEach((store: any) => {
    const crimeIncidents = store.geo?.total_crime_incidents ?? 0;

    if (crimeIncidents >= 5000) distribution.critical.count++;
    else if (crimeIncidents >= 1000) distribution.high.count++;
    else if (crimeIncidents >= 200) distribution.medium.count++;
    else if (crimeIncidents > 0) distribution.low.count++;
    else distribution.noData.count++;
  });

  // Calculate percentages
  Object.keys(distribution).forEach((key) => {
    const cat = distribution[key as keyof typeof distribution];
    cat.percentage = totalStores > 0 ? Math.round((cat.count / totalStores) * 100) : 0;
  });

  return distribution;
};

/**
 * Calculate inactive wheels data from store wheel information
 * Returns top stores with most inactive wheels
 */
const calculateInactiveWheels = (stores: any[]) => {
  const storesWithInactiveWheels = stores
    .filter((store: any) => store.wheel !== null) // Only process stores with wheel data
    .map((store: any) => {
      const wheel = store.wheel;
      const totalCarts = wheel?.last_wheel_purchase_quantity || 0;
      const activeWheels = wheel?.no_of_active_wheels || 0;
      const inactiveWheels = totalCarts - activeWheels;
      const inactivePercent = totalCarts > 0 ? ((inactiveWheels / totalCarts) * 100).toFixed(1) : '0.0';

      return {
        storeName: store.site || 'Unknown Store',
        location: store.address || 'Unknown Location',
        inactiveWheels: inactiveWheels,
        totalCarts: totalCarts,
        inactivePercent: inactivePercent + '%'
      };
    })
    .filter((s: any) => s.inactiveWheels > 0)
    .sort((a: any, b: any) => b.inactiveWheels - a.inactiveWheels)
    .slice(0, 13); // Top 13 stores

  return storesWithInactiveWheels;
};

/**
 * Calculate migrated wheels data from store wheel information
 * Returns top stores with most migrated wheels
 */
const calculateMigratedWheels = (stores: any[]) => {
  const storesWithMigratedWheels = stores
    .filter((store: any) => store.wheel !== null) // Only process stores with wheel data
    .map((store: any) => {
      const wheel = store.wheel;
      const totalCarts = wheel?.last_wheel_purchase_quantity || 0;
      const migratedWheels = (wheel?.no_of_wheels_migrated_with_other_retailer || 0) + (wheel?.no_of_wheels_migrated_within_same_retailer || 0);
      const migrationPercent = totalCarts > 0 ? ((migratedWheels / totalCarts) * 100).toFixed(1) : '0.0';

      return {
        storeName: store.site || 'Unknown Store',
        location: store.address || 'Unknown Location',
        migratedWheels: migratedWheels,
        totalCarts: totalCarts,
        migrationPercent: migrationPercent + '%'
      };
    })
    .filter((s: any) => s.migratedWheels > 0)
    .sort((a: any, b: any) => b.migratedWheels - a.migratedWheels)
    .slice(0, 10); // Top 10 stores

  return storesWithMigratedWheels;
};

/**
 * Calculate GKAP distribution from store data
 * GKAP = Percentage of stores that are customers (have customer_id)
 * Categories: Very Low (<33%), Low (<50%), Moderate (<75%), High (<90%), Very High (>=90%)
 */
const calculateGkapDistribution = (stores: any[]) => {
  const totalStores = stores.length;

  const distribution = {
    below20: { count: 0, label: 'Very Low (< 33%)', percentage: 0 },
    range20to40: { count: 0, label: 'Low (33-50%)', percentage: 0 },
    range40to60: { count: 0, label: 'Moderate (50-75%)', percentage: 0 },
    range60to80: { count: 0, label: 'High (75-90%)', percentage: 0 },
    above80: { count: 0, label: 'Very High (> 90%)', percentage: 0 }
  };

  // Categorize each store by its individual GKAP penetration percentage
  // penetration is stored as whole percentage (0-100) in the DB
  stores.forEach((store: any) => {
    const penetration = store.nearby_penetration?.penetration ?? 0;

    if (penetration >= 90) distribution.above80.count++;
    else if (penetration >= 75) distribution.range60to80.count++;
    else if (penetration >= 50) distribution.range40to60.count++;
    else if (penetration >= 33) distribution.range20to40.count++;
    else distribution.below20.count++;
  });

  // Calculate percentages
  Object.keys(distribution).forEach((key) => {
    const cat = distribution[key as keyof typeof distribution];
    cat.percentage = totalStores > 0 ? Math.round((cat.count / totalStores) * 100) : 0;
  });

  return distribution;
};

/**
 * Calculate recommendations for a store based on conditions
 * Returns reasons for each recommendation type that matches
 */
export const calculateStoreRecommendations = (
  store: any,
  brandEstimatedCarts: number = 0,
  purchekConfig?: any,
  cartManagerConfig?: any,
  containmentConfig?: any,
  subscriptionConfig?: any,
  pairedWheelConfig?: any,
  purchekActiveReadinessName?: string,
  cartManagerActiveReadinessName?: string,
  containmentActiveReadinessName?: string,
  subscriptionActiveReadinessName?: string,
  pairedWheelActiveReadinessName?: string
) => {
  return calculateRecommendations(
    store,
    brandEstimatedCarts,
    purchekConfig,
    cartManagerConfig,
    containmentConfig,
    subscriptionConfig,
    pairedWheelConfig,
    purchekActiveReadinessName,
    cartManagerActiveReadinessName,
    containmentActiveReadinessName,
    subscriptionActiveReadinessName,
    pairedWheelActiveReadinessName
  );
};

/**
 * Unified fetch for brand data - returns both stats and stores data
 * Consolidates what was previously two separate queries
 * @param brandName - The brand name to fetch data for
 */
export const fetchBrandData = async (brandName: string | string[]) => {
  try {
    const brandNames = Array.isArray(brandName) ? brandName : [brandName];
    const query = `
      query BrandData($brandNames: [String!]!) @cached {
        udm_brand(where: {name: {_in: $brandNames}}) {
          name
          # Total number of POI locations for this brand (excluding closed stores)
          total_sites: pois_aggregate(where: {closed_on: {_is_null: true}}) {
            aggregate {
              count
            }
          }
          # Number of locations that have a customer_id (acquired/known customers, excluding closed stores)
          customers: pois_aggregate(where: {_and: [{closed_on: {_is_null: true}}, {customer_id: {_is_null: false}}]}) {
            aggregate {
              count
            }
          }
          # Estimated number of carts per store for this brand
          estimated_no_of_carts_per_store
          # Individual store data to extract solution deployment counts and crime analysis (excluding closed stores)
          total_store: pois(where: {closed_on: {_is_null: true}}) {
            id
            site
            address
            zipcode
            channel
            customer_id
            lifetime_value
            latitude
            longitude
            rank
            cart_manager_rank
            purchek_rank
            containment_rank
            purchek_install_date
            containment_install_date
            cart_manager_install_date
            subscription_start_date
            paired_wheel_install_date
            first_purchase
            last_purchase
            geo {
              city
              state
              county
              state_code
              crime_rate
              total_crime_incidents
              no_of_cold_days
              no_of_hot_days
              penalty_for_cart_owner
              law_summary
              law_source
            }
            nearby_penetration {
              penetration
              purchek_customer_count
              penetration_purchek
              containment_customer_count
              penetration_containment
              plaza_cm_customer_count
              penetration_paired_wheel
              shoplifting_amount_usd
              shoplifting_incident_count
              highway_proximity
              total_density
            }
            wheel {
              last_wheel_purchase_quantity
              no_of_active_wheels
              no_of_wheels_migrated_with_other_retailer
              no_of_wheels_migrated_within_same_retailer
              single_wheel_replacement_rate
              service_frequency
            }
            video {
              total_crime_amount_usd
              incident_count
            }
            store_notes(order_by: {created_at: desc}) {
              id
              comment
              created_at
              updated_at
              user {
                id
                name
              }
            }
            updated_store_ids(order_by: {created_at: desc}, limit: 1) {
              store_id
              created_at
            }
          }
          # Sites in high crime areas (total crime incidents > 90, excluding closed stores)
          high_crime_store: pois(where: {_and: [{closed_on: {_is_null: true}}, {geo: {total_crime_incidents: {_gt: 90}}}]}) {
            site
            geo {
              total_crime_incidents
            }
          }
          # Sites in high GKAP neighbourhoods (penetration >= 50, excluding closed stores)
          sites_in_high_gkap: pois(where: {_and: [{closed_on: {_is_null: true}}, {nearby_penetration: {penetration: {_gte: 1}}}]}) {
            site
            nearby_penetration {
              penetration
            }
          }
        }
      }
    `;

    const response = await graphqlClient.request<any>(query, { brandNames });
    const brands = response.udm_brand || [];

    if (brands.length > 0) {
      let totalSites = 0;
      let acquiredSites = 0;
      let highCrimeStores = 0;
      let sitesInHighGkap = 0;
      const allStoresData: any[] = [];
      const allEnrichedStores: any[] = [];

      for (const brand of brands) {
        totalSites += brand.total_sites?.aggregate?.count || 0;
        acquiredSites += brand.customers?.aggregate?.count || 0;
        highCrimeStores += (brand.high_crime_store || []).length;
        sitesInHighGkap += (brand.sites_in_high_gkap || []).length;

        const estimatedCartsPerStore = brand.estimated_no_of_carts_per_store || 0;
        const storesData = brand.total_store || [];
        allStoresData.push(...storesData);

        const enrichedStores = storesData.map((store: any) => ({
          ...store,
          estimated_no_of_carts_per_store: estimatedCartsPerStore,
          brand_name: brand.name,
          plaza_cm_customer_count: store.nearby_penetration?.plaza_cm_customer_count ?? null,
          purchekCustomerCount: store.nearby_penetration?.purchek_customer_count ?? 0,
          purchekPenetration: store.nearby_penetration?.penetration_purchek ?? 0,
          containmentCustomerCount: store.nearby_penetration?.containment_customer_count ?? 0,
          containmentPenetration: store.nearby_penetration?.penetration_containment ?? 0,
          pairedWheelPenetration: store.nearby_penetration?.penetration_paired_wheel ?? 0,
          singleWheelReplacementRate: store.wheel?.single_wheel_replacement_rate ?? 0,
          serviceFrequency: store.wheel?.service_frequency ?? 0,
          crimeIncidents: store.geo?.total_crime_incidents ?? 0,
          coldDays: store.geo?.no_of_cold_days ?? 0,
          hotDays: store.geo?.no_of_hot_days ?? 0,
          shopliftingAmountUsd: store.nearby_penetration?.shoplifting_amount_usd ?? null,
          shopliftingIncidentCount: store.nearby_penetration?.shoplifting_incident_count ?? null,
          totalCrimeAmountUsd: store.video?.total_crime_amount_usd ?? null,
          videoIncidentCount: store.video?.incident_count ?? null,
          highwayProximity: store.nearby_penetration?.highway_proximity ?? null,
          totalDensity: store.nearby_penetration?.total_density ?? null
        }));
        allEnrichedStores.push(...enrichedStores);
      }

      // Count solution deployments from combined store data
      const solutions = {
        purchek: allStoresData.filter((s: any) => s.purchek_install_date).length,
        cartContainment: allStoresData.filter((s: any) => s.containment_install_date).length,
        cartManager: allStoresData.filter((s: any) => s.cart_manager_install_date).length,
        subscription: allStoresData.filter((s: any) => s.subscription_start_date).length,
        faceFirst: allStoresData.filter((s: any) => s.paired_wheel_install_date).length
      };

      // Calculate distributions from combined store data
      const crimeDistribution = calculateCrimeDistribution(allStoresData);
      const gkapDistribution = calculateGkapDistribution(allStoresData);
      const inactiveWheelsData = calculateInactiveWheels(allStoresData);
      const migratedWheelsData = calculateMigratedWheels(allStoresData);

      return {
        stats: {
          name: brands.map((b: any) => b.name).join(', '),
          totalSites,
          acquiredSites,
          estimatedCartsPerStore: brands[0]?.estimated_no_of_carts_per_store || 0,
          highCrimeStores,
          sitesInHighGkap,
          solutions,
          crimeDistribution,
          gkapDistribution,
          inactiveWheelsData,
          migratedWheelsData
        },
        stores: allEnrichedStores as BrandStore[]
      };
    }

    return {
      stats: {
        name: 'Unknown Brand',
        totalSites: 0,
        acquiredSites: 0,
        estimatedCartsPerStore: 0,
        solutions: {
          purchek: 0,
          cartContainment: 0,
          cartManager: 0,
          subscription: 0,
          faceFirst: 0
        }
      },
      stores: []
    };
  } catch (error) {
    console.error('fetchBrandData error', error);
    return {
      stats: {
        name: 'Unknown Brand',
        totalSites: 0,
        acquiredSites: 0,
        estimatedCartsPerStore: 0,
        solutions: {
          purchek: 0,
          cartContainment: 0,
          cartManager: 0,
          subscription: 0,
          faceFirst: 0
        }
      },
      stores: []
    };
  }
};

/**
 * Legacy function - kept for backward compatibility
 * Now uses fetchBrandData internally
 * @deprecated Use fetchBrandData instead
 */
export const fetchBrandStats = async (brandName: string) => {
  const { stats } = await fetchBrandData(brandName);
  return stats;
};

/**
 * Fetch location details (latitude, longitude) for a specific store by POI ID
 * Called when user clicks on a store to view its location on the map
 */
export const fetchStoreLocation = async (poiId: string): Promise<{ latitude: number | null; longitude: number | null } | null> => {
  try {
    const query = `
      query GetStoreLocation($poiId: String!) @cached {
        udm_poi(where: { id: { _eq: $poiId } }) {
          latitude
          longitude
          brand_name
        }
      }
    `;

    const response = await graphqlClient.request<any>(query, { poiId });
    const poi = response.udm_poi?.[0];

    if (poi) {
      return {
        latitude: poi.latitude || null,
        longitude: poi.longitude || null
      };
    }

    return null;
  } catch (error) {
    console.error('fetchStoreLocation error', error);
    return null;
  }
};

/**
 * Read store notes
 */
export interface StoreNote {
  id: string;
  comment: string;
  created_at?: string;
  updated_at?: string;
  user?: {
    id: string;
    name: string;
  };
}

export const fetchStoreNotes = async (poiId: string): Promise<StoreNote[]> => {
  try {
    console.debug('fetchStoreNotes - requesting poiId:', poiId);
    const query = `
      query GetStoreNotes($poi_id: String!) {
        udm_poi(where: {id: {_eq: $poi_id}}) {
          store_notes(order_by: {created_at: desc}) {
            id
            comment
            created_at
            updated_at
            user {
              id
              name
            }
          }
        }
      }
    `;

    const response = await graphqlClient.request<{
      udm_poi: Array<{
        store_notes: StoreNote[];
      }>;
    }>(query, {
      poi_id: poiId
    });

    console.debug('fetchStoreNotes response:', response);
    const notes = response.udm_poi && response.udm_poi[0]?.store_notes ? response.udm_poi[0].store_notes : [];
    console.debug('fetchStoreNotes store_notes array:', notes);
    return notes;
  } catch (error: any) {
    console.error('fetchStoreNotes error', error);
    return [];
  }
};

/**
 * Submit store note
 */
export const submitStoreNote = async (
  poiId: string,
  userId: string,
  comment: string
): Promise<StoreNote | null> => {
  try {
    console.debug('submitStoreNote - poiId:', poiId, 'userId:', userId, 'comment:', comment);
    const mutation = `
      mutation SubmitStoreNote($poiId: String!, $userId: uuid!, $comment: String!) {
        insert_app_store_notes_one(object: {poi_id: $poiId, user_id: $userId, comment: $comment}) {
          id
          comment
          created_at
          updated_at
          user {
            id
            name
          }
        }
      }
    `;

    const response = await graphqlClient.request<{
      insert_app_store_notes_one: StoreNote;
    }>(mutation, {
      poiId,
      userId,
      comment
    });

    console.debug('submitStoreNote response:', response);
    console.debug('submitStoreNote insert_app_store_notes_one:', response.insert_app_store_notes_one);
    return response.insert_app_store_notes_one || null;
  } catch (error: any) {
    console.error('submitStoreNote error', error);
    return null;
  }
};

/**
 * Update store note
 */
export const updateStoreNote = async (
  noteId: string,
  comment: string
): Promise<StoreNote | null> => {
  try {
    const mutation = `
      mutation UpdateStoreNote($id: uuid!, $comment: String!) {
        update_app_store_notes_by_pk(pk_columns: {id: $id}, _set: {comment: $comment}) {
          id
          comment
          created_at
          updated_at
          user {
            id
            name
          }
        }
      }
    `;

    const response = await graphqlClient.request<{
      update_app_store_notes_by_pk: StoreNote;
    }>(mutation, {
      id: noteId,
      comment
    });

    return response.update_app_store_notes_by_pk || null;
  } catch (error: any) {
    console.error('updateStoreNote error', error);
    return null;
  }
};

/**
 * Delete store note
 */
export const deleteStoreNote = async (noteId: string): Promise<boolean> => {
  try {
    const mutation = `
      mutation DeleteStoreNote($id: uuid!) {
        delete_app_store_notes_by_pk(id: $id) {
          id
        }
      }
    `;

    await graphqlClient.request<{
      delete_app_store_notes_by_pk: { id: string };
    }>(mutation, {
      id: noteId
    });

    return true;
  } catch (error: any) {
    console.error('deleteStoreNote error', error);
    return false;
  }
};

export default fetchBrandStores;
